/**
 * ReimbursementsFilters – slide-out filter drawer for the reimbursements list.
 * Mirrors the PropertyFilters / UserFilters drawer pattern (right-side panel,
 * backdrop, scrollable content, footer with Reset All / Done).
 */
import { useEffect, useMemo, type ReactNode, type SelectHTMLAttributes } from 'react'
import { ChevronDown, Filter, X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useUserRole } from '@/hooks/useUserRole'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import {
  AdvancedDateRangePicker,
  getAngularSystemAnnouncementPresets,
} from '@/components/ui/AdvancedDateRangePicker'
import {
  useOrganizationAutocomplete,
  useCompanyAutocomplete,
  usePropertyAutocomplete,
} from '../hooks/useAutocomplete'
import {
  RENTAL_STATUSES,
  DEFAULT_STATUSES,
  CLAIM_STATUSES,
  DOCUMENT_STATUSES,
  REVIEW_DECISION_OPTIONS,
  PAST_CUSTOMER_DROPDOWN,
  LIBERTY_ROLES,
} from '../constants'
import { parseLocalApiDate, toApiDate } from '../utils/formatters'
import { getResidentLeaseStartPresetRanges } from '@/features/resident/utils/residentDateUtils'
import type { ReimbursementsFiltersProps } from '../types'

const LABEL_CLASS =
  'block text-xs font-semibold text-slate-500 uppercase tracking-wider'

function isFilterValueSelected(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (value === '0' || value === '1') return false // Resident Status placeholders / defaults
  return true
}

function getSearchableSelectClass(value: unknown): string {
  const bgClass = isFilterValueSelected(value) ? 'bg-slate-50' : 'bg-white'
  return `h-12 min-h-12 rounded-xl border-slate-200 ${bgClass} px-4 hover:bg-white focus:border-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]/15`
}

/** Native `<select>` matching PropertyFilters rounding (appearance-none + custom chevron). */
function FilterNativeSelect({
  children,
  value,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const bgClass = isFilterValueSelected(value) ? 'bg-slate-50' : 'bg-white'
  return (
    <div className="relative">
      <select
        {...props}
        value={value}
        className={`h-12 w-full appearance-none rounded-xl border border-slate-200 ${bgClass} pl-4 pr-10 text-sm outline-none transition-all hover:bg-white focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/15 disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
    </div>
  )
}
function toMultiOptions(map: Record<string, string>) {
  return Object.entries(map).map(([value, label]) => ({ value, label }))
}



export function ReimbursementsFilters({
  filters,
  onFiltersChange,
  onReset,
  isOpen,
  onClose,
}: ReimbursementsFiltersProps) {
  const { roleName, orgId, orgName, companyId, companyName, propertyId, propertyName } = useUserRole()
  const isLibertyRole = useMemo(
    () => (roleName ? LIBERTY_ROLES.includes(roleName as (typeof LIBERTY_ROLES)[number]) : false),
    [roleName],
  )
  const isExternalInsurance = roleName === 'EXTERNAL_INSURANCE'

  const orgReadOnly = orgId !== null && orgId !== undefined && !isLibertyRole
  const companyReadOnly = companyId !== null && companyId !== undefined && !isLibertyRole
  const propertyReadOnly = propertyId !== null && propertyId !== undefined && !isLibertyRole

  const initialOrgOptions = useMemo(() => {
    if (filters.org_id && filters.org_name) {
      return [{ value: String(filters.org_id), label: filters.org_name }]
    }
    if (orgId && orgName) {
      return [{ value: String(orgId), label: orgName }]
    }
    if (isLibertyRole && String(filters.org_id) === '1') {
      return [{ value: '1', label: orgName || 'Liberty Rent' }]
    }
    return undefined
  }, [isLibertyRole, filters.org_id, filters.org_name, orgId, orgName])

  const initialCompanyOptions = useMemo(() => {
    if (filters.companyId && filters.company_name) {
      return [{ value: String(filters.companyId), label: filters.company_name }]
    }
    if (companyId && companyName) {
      return [{ value: String(companyId), label: companyName }]
    }
    return undefined
  }, [filters.companyId, filters.company_name, companyId, companyName])

  const initialPropertyOptions = useMemo(() => {
    if (filters.propertyId && filters.property_name) {
      return [{ value: String(filters.propertyId), label: filters.property_name }]
    }
    if (propertyId && propertyName) {
      return [{ value: String(propertyId), label: propertyName }]
    }
    return undefined
  }, [filters.propertyId, filters.property_name, propertyId, propertyName])

  const orgAuto = useOrganizationAutocomplete(initialOrgOptions)
  const companyAuto = useCompanyAutocomplete(filters.org_id, initialCompanyOptions)
  const propertyAuto = usePropertyAutocomplete(filters.org_id, filters.companyId, initialPropertyOptions)

  const rentalStatusOptions = useMemo(
    () => (isExternalInsurance ? [] : toMultiOptions(RENTAL_STATUSES)),
    [isExternalInsurance],
  )
  const defaultStatusOptions = useMemo(
    () => (isExternalInsurance ? [] : toMultiOptions(DEFAULT_STATUSES)),
    [isExternalInsurance],
  )
  const claimStatusOptions = useMemo(
    () =>
      isExternalInsurance
        ? [{ value: 'EXTERNAL_REVIEW', label: 'Level 3 Review' }]
        : toMultiOptions(CLAIM_STATUSES),
    [isExternalInsurance],
  )
  const reviewDecisionOptions = useMemo(
    () =>
      isExternalInsurance
        ? []
        : REVIEW_DECISION_OPTIONS.map((label) => ({
            value: label,
            label,
          })),
    [isExternalInsurance],
  )

  // Angular `loadCompany` auto-select: if exactly one company is returned, auto-select it
  useEffect(() => {
    if (!filters.companyId && companyAuto.options.length === 1 && !companyAuto.loading) {
      const single = companyAuto.options[0]
      onFiltersChange({
        companyId: String(single.value),
        company_name: single.label,
      })
    }
  }, [companyAuto.options, companyAuto.loading, filters.companyId, onFiltersChange])

  // Angular `loadProperty` auto-select: if exactly one property is returned, auto-select it
  useEffect(() => {
    if (!filters.propertyId && propertyAuto.options.length === 1 && !propertyAuto.loading) {
      const single = propertyAuto.options[0]
      onFiltersChange({
        propertyId: String(single.value),
        property_name: single.label,
      })
    }
  }, [propertyAuto.options, propertyAuto.loading, filters.propertyId, onFiltersChange])

  const reimbursementRangePresets = useMemo(
    () => getAngularSystemAnnouncementPresets(),
    [],
  )
  const leaseStartPresets = useMemo(() => getResidentLeaseStartPresetRanges(), [])

  // Escape key closes drawer
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useBodyScrollLock(isOpen)

  return (
    <>
      <div
        data-testid="reimbursements-filters-backdrop"
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[2147483646] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        data-testid="reimbursements-filters-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:max-w-sm bg-white shadow-2xl z-[2147483647] transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-slate-800">
            <Filter className="h-5 w-5 text-[rgb(21_151_255)]" />
            <h2 className="text-lg font-bold">Filters</h2>
          </div>
          <button
            type="button"
            data-testid="reimbursements-filters-close-button"
            onClick={onClose}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-2">
            <label className={LABEL_CLASS}>Organization</label>
            <SearchableSelect
              options={orgAuto.options}
              value={filters.org_id ? String(filters.org_id) : ''}
              onChange={(val) => {
                const v = Array.isArray(val) ? (val[0] ?? '') : val
                const opt = orgAuto.options.find((o) => String(o.value) === String(v))
                onFiltersChange({
                  org_id: v,
                  org_name: opt?.label,
                  companyId: '',
                  company_name: undefined,
                  propertyId: '',
                  property_name: undefined,
                })
                if (v) {
                  companyAuto.search('')
                }
              }}
              onSearch={orgAuto.search}
              onOpen={() => {
                if (orgAuto.options.length <= 1) orgAuto.search('')
              }}
              loading={orgAuto.loading}
              placeholder="All Organizations"
              disabled={orgReadOnly}
              triggerDataTestId="reimbursements-filters-organization"
              className={getSearchableSelectClass(filters.org_id)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Company</label>
            <SearchableSelect
              options={companyAuto.options}
              value={filters.companyId ? String(filters.companyId) : ''}
              onChange={(val) => {
                const v = Array.isArray(val) ? (val[0] ?? '') : val
                const opt = companyAuto.options.find((o) => String(o.value) === String(v))
                onFiltersChange({
                  companyId: v,
                  company_name: opt?.label,
                  propertyId: '',
                  property_name: undefined,
                })
                if (v) {
                  propertyAuto.search('')
                }
              }}
              onSearch={companyAuto.search}
              onOpen={() => {
                if (companyAuto.options.length === 0) companyAuto.search('')
              }}
              loading={companyAuto.loading}
              placeholder="All companies"
              disabled={companyReadOnly}
              triggerDataTestId="reimbursements-filters-company"
              className={getSearchableSelectClass(filters.companyId)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Property</label>
            <SearchableSelect
              options={propertyAuto.options}
              value={filters.propertyId ? String(filters.propertyId) : ''}
              onChange={(val) => {
                const v = Array.isArray(val) ? (val[0] ?? '') : val
                const opt = propertyAuto.options.find((o) => String(o.value) === String(v))
                onFiltersChange({
                  propertyId: v,
                  property_name: opt?.label,
                })
              }}
              onSearch={propertyAuto.search}
              onOpen={() => {
                if (propertyAuto.options.length === 0) propertyAuto.search('')
              }}
              loading={propertyAuto.loading}
              placeholder="All properties"
              disabled={propertyReadOnly}
              triggerDataTestId="reimbursements-filters-property"
              className={getSearchableSelectClass(filters.propertyId)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Rental Status</label>
            <SearchableSelect
              options={rentalStatusOptions}
              value={filters.rentalStatus ?? []}
              onChange={(val) => {
                const vals = Array.isArray(val) ? val : val ? [val] : []
                onFiltersChange({ rentalStatus: vals })
              }}
              placeholder="All"
              multiple
              selectedChipsLayout="flex-wrap"
              triggerDataTestId="reimbursements-filters-rentalStatus"
              className={getSearchableSelectClass(filters.rentalStatus)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Progress</label>
            <SearchableSelect
              options={defaultStatusOptions}
              value={filters.defaultStatus ?? []}
              onChange={(val) => {
                const vals = Array.isArray(val) ? val : val ? [val] : []
                onFiltersChange({ defaultStatus: vals })
              }}
              placeholder="All"
              multiple
              selectedChipsLayout="flex-wrap"
              triggerDataTestId="reimbursements-filters-defaultStatus"
              className={getSearchableSelectClass(filters.defaultStatus)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Reimbursement Status</label>
            <SearchableSelect
              options={claimStatusOptions}
              value={filters.claim_status ?? []}
              onChange={(val) => {
                const vals = Array.isArray(val) ? val : val ? [val] : []
                onFiltersChange({ claim_status: vals })
              }}
              placeholder="All"
              multiple
              selectedChipsLayout="flex-wrap"
              triggerDataTestId="reimbursements-filters-claimStatus"
              className={getSearchableSelectClass(filters.claim_status)}
            />
          </div>

          <div className="space-y-2">
            <label className={`${LABEL_CLASS}`}>Document Status</label>
            <FilterNativeSelect
              value={filters.document_status ?? ''}
              onChange={(e) => onFiltersChange({ document_status: e.target.value })}
              data-testid="reimbursements-filters-documentStatus"
            >
              <option value="">All</option>
              {Object.entries(DOCUMENT_STATUSES).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </FilterNativeSelect>
          </div>

          {isLibertyRole && (
            <div className="space-y-2">
              <label className={LABEL_CLASS}>Review Decision</label>
              <SearchableSelect
                options={reviewDecisionOptions}
                value={filters.review_decision ?? []}
                onChange={(val) => {
                  const vals = Array.isArray(val) ? val : val ? [val] : []
                  onFiltersChange({ review_decision: vals })
                }}
                placeholder="All"
                multiple
                selectedChipsLayout="flex-wrap"
                triggerDataTestId="reimbursements-filters-reviewDecision"
                className={getSearchableSelectClass(filters.review_decision)}
              />
            </div>
          )}

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Resident Status</label>
            <FilterNativeSelect
              value={filters.past_customer ?? '1'}
              onChange={(e) => onFiltersChange({ past_customer: e.target.value })}
              data-testid="reimbursements-filters-pastCustomer"
            >
              {PAST_CUSTOMER_DROPDOWN.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </FilterNativeSelect>
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Reimbursement Request Date</label>
            <AdvancedDateRangePicker
              startDate={parseLocalApiDate(filters.notification_fromDt)}
              endDate={parseLocalApiDate(filters.notification_toDt)}
              onChange={(start, end) =>
                onFiltersChange({
                  notification_fromDt: toApiDate(start),
                  notification_toDt: toApiDate(end),
                })
              }
              displayRangeFormat="angularMmddyy"
              customPresets={reimbursementRangePresets}
              presetsOnTop
              monthsShown={2}
              stackMonthsVertically
              dropdownPortalToBody
              dropdownPortalZIndex={2147483647}
              dropdownPortalCenterInFilterDrawer
              triggerClassName={getSearchableSelectClass(filters.notification_fromDt || filters.notification_toDt)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Lease Start Date</label>
            <AdvancedDateRangePicker
              startDate={parseLocalApiDate(filters.lease_start_fromDt)}
              endDate={parseLocalApiDate(filters.lease_start_toDt)}
              onChange={(start, end) =>
                onFiltersChange({
                  lease_start_fromDt: toApiDate(start),
                  lease_start_toDt: toApiDate(end),
                })
              }
              displayRangeFormat="angularMmddyy"
              customPresets={leaseStartPresets}
              presetsOnTop
              monthsShown={2}
              stackMonthsVertically
              dropdownPortalToBody
              dropdownPortalZIndex={2147483647}
              dropdownPortalCenterInFilterDrawer
              triggerClassName={getSearchableSelectClass(filters.lease_start_fromDt || filters.lease_start_toDt)}
            />
          </div>

          <div className="space-y-2">
            <label className={LABEL_CLASS}>Reimbursement Status Date</label>
            <AdvancedDateRangePicker
              startDate={parseLocalApiDate(filters.claim_status_fromDt)}
              endDate={parseLocalApiDate(filters.claim_status_toDt)}
              onChange={(start, end) =>
                onFiltersChange({
                  claim_status_fromDt: toApiDate(start),
                  claim_status_toDt: toApiDate(end),
                })
              }
              displayRangeFormat="angularMmddyy"
              customPresets={reimbursementRangePresets}
              presetsOnTop
              monthsShown={2}
              stackMonthsVertically
              dropdownPortalToBody
              dropdownPortalZIndex={2147483647}
              dropdownPortalCenterInFilterDrawer
              triggerClassName={getSearchableSelectClass(filters.claim_status_fromDt || filters.claim_status_toDt)}
            />
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            data-testid="reimbursements-filters-reset-button"
            onClick={onReset}
            className="filter-drawer-reset-all"
          >
            Reset All
          </button>
          <button
            type="button"
            data-testid="reimbursements-filters-done-button"
            onClick={onClose}
            className="flex-1 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-semibold py-2.5 rounded-lg shadow-md shadow-[rgba(21,151,255,0.22)] transition-all text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}
